import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ActivationState, Client } from "@stomp/stompjs";
import { AuthApi, Api_url } from "../../network/api/NETWORKCONSTS";
import { WebSocketContext } from "./WebSocketContext";
import { handleCoBrowseControl, stopRecording } from "./coBrowseManager";
import { APP_NAME } from "./constants";
import { buildStompConnectHeaders } from "./stompHeaders";
import { createChatTransport } from "./createChatTransport";

export { APP_NAME };

export const WebSocketProvider = ({ children }) => {
    const currentUser = useSelector((state) => state.auth.user);
    const token = useSelector((state) => state.auth.token);
    const myUserId = currentUser?.Id || currentUser?.id;

    const [status, setStatus] = useState(ActivationState.INACTIVE);
    const clientRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const connectingRef = useRef(false);
    const chatListenersRef = useRef(new Set());
    const tokenRef = useRef(token);
    const myUserIdRef = useRef(myUserId);
    const hasConnectedOnceRef = useRef(false);
    const sendCoBrowseEventsRef = useRef(null);

    tokenRef.current = token;
    myUserIdRef.current = myUserId;

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const sendCoBrowseEvents = useCallback((events) => {
        const uid = myUserIdRef.current;
        const client = clientRef.current;
        // Never send before CONNECT / empty batch / wrong user
        if (!client?.connected || !uid) return;
        if (!Array.isArray(events) || events.length === 0) return;
        const capped = events.length > 500 ? events.slice(0, 500) : events;
        try {
            client.publish({
                destination: `/app/cobrowse.events/${uid}`,
                body: JSON.stringify({ Events: capped }),
            });
        } catch (e) {
            console.warn("[CoBrowse][user] events-publish-failed", e);
        }
    }, []);
    sendCoBrowseEventsRef.current = sendCoBrowseEvents;

    const deactivate = useCallback(() => {
        stopRecording();
        clearReconnectTimer();
        connectingRef.current = false;
        hasConnectedOnceRef.current = false;
        if (clientRef.current) {
            try {
                clientRef.current.deactivate();
            } catch (e) {
                // ignore
            }
            clientRef.current = null;
        }
        if (isMountedRef.current) {
            setStatus(ActivationState.INACTIVE);
        }
    }, [clearReconnectTimer]);

    const scheduleReconnect = useCallback((delayMs = 8000) => {
        if (reconnectTimerRef.current) return;
        reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!isMountedRef.current) return;
            if (!tokenRef.current || !myUserIdRef.current) return;
            if (clientRef.current?.connected || connectingRef.current) return;
            // eslint-disable-next-line no-use-before-define
            activateRef.current?.();
        }, delayMs);
    }, []);

    const activateRef = useRef(null);

    const activate = useCallback(() => {
        if (!isMountedRef.current) return;

        const currentToken = tokenRef.current;
        const uid = myUserIdRef.current;
        if (!currentToken || !uid) {
            deactivate();
            return;
        }

        // Already up or mid-handshake — do not tear down / recreate
        if (clientRef.current?.connected) return;
        if (connectingRef.current) return;
        if (clientRef.current?.active) return;

        if (clientRef.current) {
            try {
                clientRef.current.deactivate();
            } catch (e) {
                // ignore
            }
            clientRef.current = null;
        }

        connectingRef.current = true;
        const endpoint = AuthApi.BASECHATURL + Api_url.Chat.endpoint;

        const client = new Client({
            // Prefer native websocket transport (xhr-polling kills CoBrowse latency)
            webSocketFactory: () => createChatTransport(endpoint),
            // Let STOMP own reconnect; avoid racing with our timers
            reconnectDelay: 8000,
            heartbeatIncoming: 0,
            heartbeatOutgoing: 0,
            // recent-visits: AppName must be present on every CONNECT (incl. STOMP reconnect)
            connectHeaders: buildStompConnectHeaders(currentToken),
            beforeConnect: async () => {
                const t = tokenRef.current;
                if (!t) throw new Error("Missing token before STOMP connect");
                client.connectHeaders = buildStompConnectHeaders(t);
            },
            onConnect: () => {
                connectingRef.current = false;
                if (!isMountedRef.current) return;
                setStatus(ActivationState.ACTIVE);
                clearReconnectTimer();

                const connectedUserId = myUserIdRef.current;
                if (!connectedUserId) return;

                const isReconnect = hasConnectedOnceRef.current;
                hasConnectedOnceRef.current = true;

                // On reconnect: stop recording; wait for a fresh START
                stopRecording();

                client.subscribe(`/chat/support/${connectedUserId}`, (msg) => {
                    if (!msg.body) return;
                    try {
                        const parsed = JSON.parse(msg.body);
                        chatListenersRef.current.forEach((listener) => listener(parsed));
                    } catch (e) {
                        const fallback = { Sender: "ADMIN", Message: msg.body };
                        chatListenersRef.current.forEach((listener) => listener(fallback));
                    }
                });

                const destination = `/chat/cobrowse/control/${connectedUserId}`;
                client.subscribe(destination, (msg) => {
                    if (!msg.body) return;
                    try {
                        const parsed = JSON.parse(msg.body);
                        handleCoBrowseControl(parsed, {
                            myUserId: connectedUserId,
                            sendEvents: (events) => sendCoBrowseEventsRef.current?.(events),
                        });
                    } catch (e) {
                        console.warn("[CoBrowse][user] control-parse-failed", e);
                    }
                });

                if (isReconnect) {
                    console.info("[CoBrowse][user] re-subscribed-after-reconnect", {
                        myUserId: connectedUserId,
                        destination,
                    });
                } else {
                    console.info("[CoBrowse][user] control-subscribed", { destination });
                }
            },
            onChangeState: (newState) => {
                if (!isMountedRef.current) return;
                setStatus(newState);
                if (newState === ActivationState.ACTIVE) {
                    connectingRef.current = false;
                }
            },
            onDisconnect: () => {
                connectingRef.current = false;
                if (!isMountedRef.current) return;
                setStatus(ActivationState.INACTIVE);
                stopRecording();
            },
            onStompError: (frame) => {
                connectingRef.current = false;
                console.warn(
                    "[CoBrowse][user] stomp-error",
                    frame?.headers?.message || frame?.headers?.Message || frame?.body
                );
                if (!isMountedRef.current) return;
                setStatus(ActivationState.INACTIVE);
                stopRecording();
            },
            onWebSocketClose: () => {
                connectingRef.current = false;
                if (!isMountedRef.current) return;
                setStatus(ActivationState.INACTIVE);
                stopRecording();
                // Fallback only — STOMP reconnectDelay handles most cases
                scheduleReconnect(10000);
            },
            onWebSocketError: () => {
                connectingRef.current = false;
            },
        });

        clientRef.current = client;
        client.activate();
    }, [deactivate, clearReconnectTimer, scheduleReconnect]);

    activateRef.current = activate;

    const reactive = useCallback(() => {
        if (!isMountedRef.current) return;
        if (!tokenRef.current || !myUserIdRef.current) return;
        if (clientRef.current?.connected || connectingRef.current) return;
        activate();
    }, [activate]);

    const sendChatMessage = useCallback((msg, onStatus) => {
        const uid = myUserIdRef.current;
        if (clientRef.current?.connected && uid) {
            clientRef.current.publish({
                destination: `/app/support.chat/${uid}`,
                body: JSON.stringify(msg),
            });
            onStatus?.({ message: msg, status: "sent" });
        } else {
            onStatus?.({ message: msg, status: "failed" });
        }
    }, []);

    const addChatListener = useCallback((listener) => {
        chatListenersRef.current.add(listener);
        return () => {
            chatListenersRef.current.delete(listener);
        };
    }, []);

    // Connect only when auth identity changes — do NOT depend on activate identity
    // (that was causing disconnect/reconnect loops).
    useEffect(() => {
        isMountedRef.current = true;
        if (token && myUserId) {
            activateRef.current?.();
        } else {
            deactivate();
        }
        return () => {
            isMountedRef.current = false;
            deactivate();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, myUserId]);

    // Soft recovery when tab comes back / network returns — never if already connected
    useEffect(() => {
        const tryReconnect = () => {
            if (!tokenRef.current || !myUserIdRef.current) return;
            if (clientRef.current?.connected || connectingRef.current) return;
            if (clientRef.current?.active) return;
            activateRef.current?.();
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") tryReconnect();
        };

        // recent-visits: close presence duration on tab close / bfcache
        const onPageHide = () => {
            stopRecording();
            if (clientRef.current) {
                try {
                    clientRef.current.deactivate();
                } catch (e) {
                    // ignore
                }
            }
        };

        window.addEventListener("online", tryReconnect);
        window.addEventListener("pagehide", onPageHide);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            window.removeEventListener("online", tryReconnect);
            window.removeEventListener("pagehide", onPageHide);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const contextValue = {
        status,
        reactive,
        sendChatMessage,
        addChatListener,
        myUserId,
    };

    return (
        <WebSocketContext.Provider value={contextValue}>
            {children}
        </WebSocketContext.Provider>
    );
};
